/**
 * DailyChecksSettingsView.js
 *
 * FT-80 Phase 3: Purpose-built settings view for Daily Checks.
 * Supports: enable/disable categories, edit check items, add custom categories, reset to defaults.
 * Uses useDailyChecksConfig hook for vessel-scoped persistence.
 *
 * IMPORTANT: Edit/Remove buttons are ALWAYS VISIBLE (no hover patterns - per user requirement)
 */

import React, { useState } from 'react';
import {
  Cog,
  Navigation,
  Anchor,
  Eye,
  EyeOff,
  Pencil,
  Plus,
  Trash2,
  ChevronRight,
  ChevronDown,
  Check,
  X,
  Info,
  ClipboardList,
  ClipboardCheck
} from 'lucide-react';
import { useDailyChecksConfig } from '../../../../hooks/domain/operations/useDailyChecksConfig';

// Icon mapping for categories
const CATEGORY_ICONS = {
  general: ClipboardCheck,
  engine_room: Cog,
  bridge: Navigation,
  deck: Anchor
};

/**
 * Single Check Item Row (editable) - ALWAYS VISIBLE buttons
 */
const CheckItemRow = ({ check, onEdit, onRemove, isEditing, onSave, onCancel }) => {
  const [editText, setEditText] = useState(check.text);

  if (isEditing) {
    return (
      <div className="flex items-center gap-2 py-2 px-3 bg-cyan-50 rounded-lg border border-cyan-200">
        <input
          type="text"
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          className="flex-1 px-2 py-1 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
          autoFocus
        />
        <button
          onClick={() => onSave({ ...check, text: editText.trim() })}
          disabled={!editText.trim()}
          className="p-1 text-green-600 hover:bg-green-100 rounded disabled:opacity-50"
        >
          <Check className="h-4 w-4" />
        </button>
        <button
          onClick={onCancel}
          className="p-1 text-slate-400 hover:bg-slate-100 rounded"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // ALWAYS VISIBLE edit/remove buttons - NO hover patterns
  return (
    <div className="flex items-center gap-2 py-2 px-3 bg-white rounded-lg border border-slate-200">
      <span className="flex-1 text-sm text-slate-700">{check.text}</span>
      {/* ALWAYS VISIBLE - no opacity-0 or group-hover */}
      <div className="flex items-center gap-1">
        <button
          onClick={onEdit}
          className="p-1.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded"
          title="Edit"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          onClick={onRemove}
          className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
          title="Remove"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

/**
 * Add New Check Form
 */
const AddCheckForm = ({ onAdd, onCancel }) => {
  const [text, setText] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    onAdd({
      id: `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      text: text.trim()
    });
    setText('');
  };

  return (
    <form onSubmit={handleSubmit} className="flex items-center gap-2 py-2 px-3 bg-green-50 rounded-lg border border-green-200">
      <input
        type="text"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Enter new check item..."
        className="flex-1 px-2 py-1 text-sm border border-slate-300 rounded focus:ring-2 focus:ring-green-500 focus:border-green-500"
        autoFocus
      />
      <button
        type="submit"
        disabled={!text.trim()}
        className="p-1 text-green-600 hover:bg-green-100 rounded disabled:opacity-50"
      >
        <Check className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="p-1 text-slate-400 hover:bg-slate-100 rounded"
      >
        <X className="h-4 w-4" />
      </button>
    </form>
  );
};

/**
 * Category Card with expandable checks - ALWAYS VISIBLE controls
 */
const CategoryCard = ({
  category,
  isExpanded,
  onToggleExpand,
  onToggleEnabled,
  onUpdateChecks
}) => {
  const Icon = CATEGORY_ICONS[category.id] || ClipboardList;
  const [editingCheckId, setEditingCheckId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);

  const handleSaveCheck = (updatedCheck) => {
    const newChecks = category.checks.map(c =>
      c.id === updatedCheck.id ? updatedCheck : c
    );
    onUpdateChecks(category.id, newChecks);
    setEditingCheckId(null);
  };

  const handleRemoveCheck = (checkId) => {
    const newChecks = category.checks.filter(c => c.id !== checkId);
    onUpdateChecks(category.id, newChecks);
  };

  const handleAddCheck = (newCheck) => {
    const newChecks = [...category.checks, newCheck];
    onUpdateChecks(category.id, newChecks);
    setShowAddForm(false);
  };

  return (
    <div className={`border rounded-xl overflow-hidden ${category.enabled ? 'border-slate-200' : 'border-slate-200 bg-slate-50'}`}>
      {/* Category Header */}
      <div className="flex items-center justify-between p-4 bg-slate-50 border-b border-slate-200">
        <button
          onClick={onToggleExpand}
          className="flex items-center gap-3 flex-1 text-left"
        >
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${category.enabled ? 'bg-cyan-100' : 'bg-slate-200'}`}>
            <Icon className={`h-5 w-5 ${category.enabled ? 'text-cyan-600' : 'text-slate-400'}`} />
          </div>
          <div>
            <p className={`font-medium ${category.enabled ? 'text-slate-800' : 'text-slate-500'}`}>
              {category.label}
            </p>
            <p className="text-xs text-slate-500">
              {category.checks.length} checks {!category.enabled && '(disabled)'}
            </p>
          </div>
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-slate-400 ml-auto" />
          ) : (
            <ChevronRight className="h-5 w-5 text-slate-400 ml-auto" />
          )}
        </button>

        {/* Enable/Disable Toggle - ALWAYS VISIBLE */}
        <button
          onClick={() => onToggleEnabled(category.id)}
          className={`
            ml-2 p-2 rounded-lg transition-colors
            ${category.enabled
              ? 'text-cyan-600 hover:bg-cyan-50'
              : 'text-slate-400 hover:bg-slate-100'
            }
          `}
          title={category.enabled ? 'Click to disable this category' : 'Click to enable this category'}
        >
          {category.enabled ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
        </button>
      </div>

      {/* Expanded Checks List */}
      {isExpanded && (
        <div className="p-4 space-y-2">
          {category.checks.map(check => (
            <CheckItemRow
              key={check.id}
              check={check}
              onEdit={() => setEditingCheckId(check.id)}
              onRemove={() => handleRemoveCheck(check.id)}
              isEditing={editingCheckId === check.id}
              onSave={handleSaveCheck}
              onCancel={() => setEditingCheckId(null)}
            />
          ))}

          {category.checks.length === 0 && !showAddForm && (
            <p className="text-sm text-slate-500 text-center py-4">
              No checks configured. Add your first check below.
            </p>
          )}

          {/* Add Check Form or Button */}
          {showAddForm ? (
            <AddCheckForm
              onAdd={handleAddCheck}
              onCancel={() => setShowAddForm(false)}
            />
          ) : (
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full py-2 px-3 border-2 border-dashed border-slate-300 rounded-lg
                text-slate-500 hover:border-cyan-400 hover:text-cyan-600 transition-colors
                flex items-center justify-center gap-2 text-sm"
            >
              <Plus className="h-4 w-4" />
              Add Check Item
            </button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * Add Custom Category Form
 */
const AddCategoryForm = ({ onAdd, onCancel }) => {
  const [label, setLabel] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!label.trim()) return;
    onAdd(label.trim());
    setLabel('');
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 bg-green-50 rounded-xl border-2 border-green-300">
      <p className="text-sm font-medium text-green-800 mb-2">Add Custom Category</p>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="e.g., Galley Checks, Safety Equipment"
          className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
          autoFocus
        />
        <button
          type="submit"
          disabled={!label.trim()}
          className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50"
        >
          Add
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm text-slate-600 hover:text-slate-800"
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

/**
 * Main DailyChecksSettingsView Component
 */
const DailyChecksSettingsView = ({ onImport }) => {
  const {
    categories,
    toggleCategory,
    updateCategoryChecks,
    addCategory
  } = useDailyChecksConfig();

  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [showAddCategory, setShowAddCategory] = useState(false);

  const toggleExpand = (categoryId) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const handleAddCategory = (label) => {
    addCategory(label);
    setShowAddCategory(false);
  };

  // Summary stats
  const enabledCount = categories.filter(c => c.enabled).length;
  const totalChecks = categories.reduce((sum, c) => sum + c.checks.length, 0);

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium">Daily Checks Configuration</p>
          <p className="mt-1">
            Configure which daily checks appear in the Operations tab. Toggle categories on/off,
            add custom checks, or add entirely new categories for your vessel.
          </p>
        </div>
      </div>

      {/* Summary */}
      <div className="flex items-center justify-between text-sm text-slate-600">
        <span>{enabledCount} of {categories.length} categories enabled ({totalChecks} total checks)</span>
      </div>

      {/* Category Cards */}
      <div className="space-y-3">
        {categories.map(category => (
          <CategoryCard
            key={category.id}
            category={category}
            isExpanded={expandedCategories.has(category.id)}
            onToggleExpand={() => toggleExpand(category.id)}
            onToggleEnabled={toggleCategory}
            onUpdateChecks={updateCategoryChecks}
          />
        ))}
      </div>

      {/* Add Custom Category */}
      {showAddCategory ? (
        <AddCategoryForm
          onAdd={handleAddCategory}
          onCancel={() => setShowAddCategory(false)}
        />
      ) : (
        <button
          onClick={() => setShowAddCategory(true)}
          className="w-full py-3 px-4 border-2 border-dashed border-cyan-300 rounded-xl
            text-cyan-600 hover:bg-cyan-50 hover:border-cyan-400 transition-colors
            flex items-center justify-center gap-2"
        >
          <Plus className="h-5 w-5" />
          Add Custom Category
        </button>
      )}

      {/* Audit Notice */}
      <div className="text-xs text-slate-500 text-center">
        Changes are saved automatically and will be logged for SMS compliance.
      </div>
    </div>
  );
};

export default DailyChecksSettingsView;
